import { useEffect, useState } from "react";
import axios from "axios";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { GlobalState } from "../../context/GlobalContext";
import { AuthState } from "../../context/AuthContext";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import {
	createDeviceCredential,
	forgetDeviceId,
	platformAuthenticatorAvailable,
	rememberDeviceId,
	serializeRegistration,
} from "utils/webauthn";

const DeviceSection = ({ isDefault, onSetDefault, onDefaultCleared }) => {
	const { user } = AuthState();
	const { snackBar, setSnackBar, setSnackBarOpen } = GlobalState();
	const [supported, setSupported] = useState(false);
	const [checked, setChecked] = useState(false);
	const [credentials, setCredentials] = useState([]);
	const [busy, setBusy] = useState(false);
	const [removeId, setRemoveId] = useState("");
	const [password, setPassword] = useState("");

	const showSnackBar = (message, severity = "error") => {
		setSnackBarOpen(true);
		setSnackBar({ ...snackBar, message, severity });
	};

	const loadCredentials = async () => {
		const res = await axios.get("/api/auth2/webauthn/credentials");
		const next = res?.data?.result?.credentials || [];
		setCredentials(next);
		return next;
	};

	useEffect(() => {
		let active = true;
		(async () => {
			const available = await platformAuthenticatorAvailable();
			if (!active) return;
			setSupported(available);
			try {
				await loadCredentials();
			} catch (error) {
				if (active) showSnackBar(parseErrorMessage(error));
			} finally {
				if (active) setChecked(true);
			}
		})();
		return () => {
			active = false;
		};
	}, []);

	const registerDevice = async () => {
		setBusy(true);
		try {
			const optionsRes = await axios.post("/api/auth2/webauthn/register/options");
			const credential = await createDeviceCredential(optionsRes?.data?.result);
			if (!credential) throw new Error("This device did not return a credential");
			const verifyRes = await axios.post("/api/auth2/webauthn/register/verify", {
				credential: serializeRegistration(credential),
				deviceLabel: "This device",
			});
			rememberDeviceId(user?.email, verifyRes?.data?.result?.credential_id || credential.id);
			await loadCredentials();
			showSnackBar(verifyRes?.data?.message || "This device can now sign in", "success");
		} catch (error) {
			if (error?.name === "NotAllowedError") showSnackBar("Device registration was cancelled");
			else showSnackBar(parseErrorMessage(error));
		} finally {
			setBusy(false);
		}
	};

	const removeDevice = async (row) => {
		if (!password) {
			showSnackBar("Enter your Relook password to remove this laptop");
			return;
		}
		setBusy(true);
		try {
			const res = await axios.post(`/api/auth2/webauthn/credentials/${row.id}/remove`, { password });
			forgetDeviceId(user?.email, res?.data?.result?.credential_id);
			setPassword("");
			setRemoveId("");
			const next = await loadCredentials();
			if (!next.length && isDefault) onDefaultCleared?.();
			showSnackBar(res?.data?.message || "Device removed", "success");
		} catch (error) {
			showSnackBar(parseErrorMessage(error));
		} finally {
			setBusy(false);
		}
	};

	if (!checked) return <p className="profile-copy">Checking this laptop...</p>;

	return (
		<Stack spacing={1.25}>
			<span className={`profile-status${credentials.length ? " is-on" : ""}`}>{credentials.length ? "Registered" : "Not registered"}</span>
			<p className="profile-copy">
				{supported
					? "Uses the fingerprint, PIN, or password already set on this laptop. Passkeys and security keys are not used."
					: "This browser cannot use the fingerprint, PIN, or password on this laptop."}
			</p>
			{credentials.map((row) => (
				<Box key={row.id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1.25 }}>
					<Typography variant="body2" fontWeight={700}>
						{row.device_label || "This laptop"}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						Registered {row.created_at ? new Date(row.created_at).toLocaleString() : ""}
					</Typography>
					{removeId === row.id ? (
						<Stack spacing={1} sx={{ mt: 1.25 }}>
							<TextField size="small" type="password" label="Relook password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" fullWidth />
							<Button color="warning" variant="outlined" disabled={busy} onClick={() => removeDevice(row)}>
								Remove laptop
							</Button>
							<Button
								disabled={busy}
								onClick={() => {
									setRemoveId("");
									setPassword("");
								}}>
								Cancel
							</Button>
						</Stack>
					) : (
						<Button sx={{ mt: 1 }} onClick={() => setRemoveId(row.id)}>
							Remove
						</Button>
					)}
				</Box>
			))}
			{credentials.length ? (
				isDefault ? (
					<p className="profile-default-note">Default at login</p>
				) : (
					<button type="button" className="profile-default-btn" onClick={onSetDefault}>
						Set as default
					</button>
				)
			) : null}
			{supported ? (
				<Button variant="contained" disabled={busy} onClick={registerDevice}>
					Register this laptop
				</Button>
			) : null}
		</Stack>
	);
};

export default DeviceSection;
